import { useEffect, useMemo, useRef, useState } from 'react';
import {
  salaryImportApi,
  type ImportKind,
  type ImportPreview,
  type MatchedRow,
} from '../lib/api/salary-import';
import { formatVND } from '../lib/format';

interface Props {
  open: boolean;
  kind: ImportKind | null;
  /** Override title mặc định theo kind — dùng cho context riêng (VD: Payroll page = "Import bảng lương từ Excel"). */
  titleOverride?: string;
  /** Override text banner xanh (nội dung mô tả cột + hành vi parse) */
  hintOverride?: string;
  /** Nếu set → hiển thị row template trong bước upload. */
  templateName?: string;
  templateHref?: string;
  /** Tải template qua API, dùng khi file cần được tạo/dịch vụ hoá ở backend. */
  onDownloadTemplate?: () => Promise<void> | void;
  onClose: () => void;
  onDone: () => void;
}

const TITLE: Record<ImportKind, { title: string; sub: string; hint: string }> = {
  'salary-config': {
    title: 'Import Cấu hình lương NV',
    sub: 'File "Bang Luong mới 2026" · sheet đầu tiên',
    hint: 'Cập nhật: lương thoả thuận (GROSS) + Mức lương đóng BHXH cho NV đã có trong hệ thống.',
  },
  'bank-software-nv': {
    title: 'Import STK — HITEK Software (Nhân viên)',
    sub: 'File "M_KT_DS STK NHÂN SỰ SOFTWARE" · sheet "Nhân viên"',
    hint: 'Cập nhật: Số TK, Tên NH, Chi nhánh cho NV chính thức.',
  },
  'bank-software-tts': {
    title: 'Import STK — HITEK Software (TTS)',
    sub: 'File "M_KT_DS STK NHÂN SỰ SOFTWARE" · sheet "Danh sách TTS"',
    hint: 'Cập nhật STK cho Thực tập sinh. Cột "GHI CHÚ" (trợ cấp) chỉ đọc, không ghi vào lương.',
  },
  'bank-group': {
    title: 'Import STK — HITEK Group',
    sub: 'File "M_KT_DS STK NHÂN SỰ GROUP" · sheet Sheet1',
    hint: 'Cập nhật: Số TK, Tên NH. (Cột "GHI CHÚ" trong file thực ra là CHỦ TK, không dùng.)',
  },
  'payroll-batch': {
    title: 'Import bảng lương từ Excel',
    sub: 'File "M_KT_BangLuongHitek.2026" · sheet đầu tiên',
    hint: 'Sinh bảng lương DRAFT cho kỳ hiện tại. NV master data KHÔNG bị thay đổi.',
  },
};

const FIELD_LABEL: Record<string, string> = {
  agreedSalary: 'Lương thoả thuận',
  socialInsuranceBase: 'BHXH base',
  salaryType: 'Loại lương',
  bankAccount: 'Số TK',
  bankName: 'Tên NH',
  bankBranch: 'Chi nhánh',
};

export function SalaryImportModal({ open, kind, titleOverride, hintOverride, templateName, templateHref, onDownloadTemplate, onClose, onDone }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ updated: number; skipped: number; errors: number; payrollId?: string; payrollCode?: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPreview(null);
    setSelected(new Set());
    setErr(null);
    setResult(null);
    setLoading(false);
    setCommitting(false);
    setDownloadingTemplate(false);
  }, [open, kind]);

  const meta = kind ? TITLE[kind] : null;

  // payroll-batch không có diff (chỉ tạo Payroll mới) → tất cả matched rows đều được commit
  const isPayrollBatch = kind === 'payroll-batch';
  const rowsWithChanges = useMemo(
    () => (preview?.matched ?? []).filter((r) => isPayrollBatch || Object.keys(r.changes).length > 0),
    [preview, isPayrollBatch],
  );

  // Tick default = tất cả row có changes (hoặc all cho payroll-batch).
  useEffect(() => {
    if (!preview) return;
    setSelected(new Set(rowsWithChanges.map((r) => r.employeeId)));
  }, [preview, rowsWithChanges]);

  if (!open || !kind || !meta) return null;

  const handleFile = (f: File | null) => {
    setFile(f);
    setPreview(null);
    setErr(null);
    setResult(null);
  };

  const runPreview = async () => {
    if (!file) return;
    setLoading(true);
    setErr(null);
    try {
      const p = await salaryImportApi.preview(kind, file);
      setPreview(p);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const runCommit = async () => {
    if (!preview) return;
    const rows = preview.matched
      .filter((r) => selected.has(r.employeeId) && (isPayrollBatch || Object.keys(r.changes).length > 0))
      .map((r) => ({ employeeId: r.employeeId, data: r.data }));
    if (rows.length === 0) {
      setErr(isPayrollBatch ? 'Chưa chọn NV nào để sinh bảng lương.' : 'Chưa chọn NV nào để cập nhật.');
      return;
    }
    setCommitting(true);
    setErr(null);
    try {
      const res = await salaryImportApi.commit(kind, rows);
      setResult({ updated: res.updated, skipped: res.skipped, errors: res.errors.length, payrollId: res.payrollId, payrollCode: res.payrollCode });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCommitting(false);
    }
  };

  const downloadTemplate = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!onDownloadTemplate) return;
    setDownloadingTemplate(true);
    setErr(null);
    try {
      await onDownloadTemplate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === rowsWithChanges.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rowsWithChanges.map((r) => r.employeeId)));
    }
  };

  const isStep1 = !preview && !result;
  const modalWidth = isStep1 ? 560 : 900;
  const hintText = hintOverride ?? meta.hint;

  return (
    <div className="cvm-backdrop" onClick={onClose}>
      <div
        className={`cvm-modal sim-modal ${isStep1 ? 'sim-modal-step1' : ''}`}
        style={{ width: modalWidth, maxWidth: '96vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sim-head">
          <div className="sim-head-left">
            <div className="sim-head-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
            </div>
            <div>
              <h2>{titleOverride ?? meta.title}</h2>
              <p>Upload file Excel theo template · System auto validate + preview trước import</p>
            </div>
          </div>
          <button type="button" className="sim-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="sim-body">
          {/* Step 1: chọn file — match prototype simple layout */}
          {isStep1 && (
            <>
              <div className="sim-info-banner">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round"><path d="M12 8v4M12 16h.01" /><circle cx="12" cy="12" r="10" /></svg>
                <div>{hintText}</div>
              </div>

              {templateName && (templateHref || onDownloadTemplate) && (
                <div className="sim-template-row">
                  <div className="sim-template-info">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /></svg>
                    <span>Template: <b>{templateName}</b></span>
                  </div>
                  {templateHref ? (
                    <a className="sim-template-dl" href={templateHref} download={templateName} onClick={(e) => e.stopPropagation()}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /></svg>
                      Tải template
                    </a>
                  ) : (
                    <button type="button" className="sim-template-dl" disabled={downloadingTemplate} onClick={downloadTemplate}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /></svg>
                      {downloadingTemplate ? 'Đang tải…' : 'Tải template'}
                    </button>
                  )}
                </div>
              )}

              <div
                className={`sim-dropzone ${file ? 'has-file' : ''}`}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragging'); }}
                onDragLeave={(e) => e.currentTarget.classList.remove('dragging')}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('dragging');
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleFile(f);
                }}
                onClick={() => inputRef.current?.click()}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                />
                {file ? (
                  <>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /></svg>
                    <div className="sim-dropzone-main">
                      <b>{file.name}</b>
                    </div>
                    <div className="sim-dropzone-sub">
                      {(file.size / 1024).toFixed(1)} KB · Click <button type="button" className="sim-dropzone-link" onClick={(e) => { e.stopPropagation(); handleFile(null); }}>xoá</button> để chọn file khác
                    </div>
                  </>
                ) : (
                  <>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
                    <div className="sim-dropzone-main">Kéo thả file Excel vào đây</div>
                    <div className="sim-dropzone-sub">
                      hoặc <span className="sim-dropzone-link">chọn từ máy</span>
                    </div>
                    <div className="sim-dropzone-hint">.xlsx · .xls · tối đa 20MB</div>
                  </>
                )}
              </div>
            </>
          )}

          {/* Step 2: preview — bg gray + banner + matching table */}
          {preview && !result && (
            <div className="sim-preview">
              <div className="sim-info-banner sim-info-banner-plain">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round"><path d="M12 8v4M12 16h.01" /><circle cx="12" cy="12" r="10" /></svg>
                <div>{hintText}</div>
              </div>
              <PreviewSummary
                preview={preview}
                selectedCount={selected.size}
                changedCount={rowsWithChanges.length}
              />

              {rowsWithChanges.length > 0 && (
                <>
                  <div style={sectionTitle}>
                    <span>{isPayrollBatch ? 'Sẽ thêm vào bảng lương' : 'Sẽ cập nhật'} ({rowsWithChanges.length})</span>
                    <button type="button" className="pay-btn-outline" onClick={toggleAll}>
                      {selected.size === rowsWithChanges.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                    </button>
                  </div>
                  <div style={tableWrap}>
                    <table style={tblStyle}>
                      <thead>
                        <tr>
                          <th style={{ width: 32 }}></th>
                          <th style={{ width: 90 }}>Mã NV</th>
                          <th>Họ tên</th>
                          <th>Thay đổi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rowsWithChanges.map((r) => (
                          <ChangeRow
                            key={r.employeeId}
                            row={r}
                            checked={selected.has(r.employeeId)}
                            onToggle={() => toggle(r.employeeId)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {preview.matched.length - rowsWithChanges.length > 0 && (
                <div style={{ marginTop: 10, color: '#16a34a', fontSize: 13 }}>
                  ✓ {preview.matched.length - rowsWithChanges.length} NV khớp nhưng không có thay đổi (đã đúng dữ liệu).
                </div>
              )}

              {preview.unmatched.length > 0 && (
                <>
                  <div style={{ ...sectionTitle, color: '#b91c1c' }}>
                    Không match được ({preview.unmatched.length})
                  </div>
                  <div style={tableWrap}>
                    <table style={tblStyle}>
                      <thead>
                        <tr>
                          <th style={{ width: 60 }}>Dòng</th>
                          <th>Họ tên (file)</th>
                          <th>Lý do</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.unmatched.map((r) => (
                          <tr key={r.rowIndex}>
                            <td style={{ color: '#64748b' }}>{r.rowIndex}</td>
                            <td>{r.rawName}</td>
                            <td style={{ color: '#b91c1c' }}>{r.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                    Muốn thêm NV mới? Vào <b>Quản lý NV</b> → chọn từ HR directory rồi import.
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3: result */}
          {result && (
            <div style={{ marginTop: 20, padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac' }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#166534' }}>Import xong ✓</div>
              <div style={{ marginTop: 6, fontSize: 14, color: '#166534' }}>
                {isPayrollBatch && result.payrollCode ? (
                  <>Đã sinh bảng lương <b>{result.payrollCode}</b> với <b>{result.updated}</b> NV.</>
                ) : (
                  <>Đã cập nhật <b>{result.updated}</b> NV</>
                )}
                {result.skipped > 0 && <> · bỏ qua <b>{result.skipped}</b></>}
                {result.errors > 0 && <> · lỗi <b style={{ color: '#b91c1c' }}>{result.errors}</b></>}
              </div>
            </div>
          )}

          {err && <div className="cvm-error" style={{ marginTop: 12 }}>{err}</div>}
        </div>

        <div className="sim-foot">
          <button type="button" className="sim-btn-cancel" onClick={onClose} disabled={loading || committing}>
            {result ? 'Đóng' : 'Hủy'}
          </button>
          {isStep1 && (
            <button
              type="button"
              className="sim-btn-primary"
              onClick={runPreview}
              disabled={!file || loading}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
              {loading ? 'Đang phân tích…' : 'Preview + Import'}
            </button>
          )}
          {preview && !result && (
            <button
              type="button"
              className="sim-btn-primary"
              onClick={runCommit}
              disabled={committing || selected.size === 0}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
              {committing
                ? (isPayrollBatch ? 'Đang sinh bảng lương…' : 'Đang cập nhật…')
                : (isPayrollBatch ? `Sinh bảng lương (${selected.size} NV)` : `Cập nhật ${selected.size} NV`)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewSummary({
  preview,
  selectedCount,
  changedCount,
}: {
  preview: ImportPreview;
  selectedCount: number;
  changedCount: number;
}) {
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13 }}>
      <Stat label="Sheet" value={preview.sheetName} />
      <Stat label="Tổng row" value={String(preview.totalRows)} />
      <Stat label="Match" value={String(preview.matched.length)} tone="green" />
      <Stat label="Không match" value={String(preview.unmatched.length)} tone={preview.unmatched.length > 0 ? 'red' : 'gray'} />
      <Stat label="Có thay đổi" value={String(changedCount)} tone="blue" />
      <Stat label="Đã chọn" value={String(selectedCount)} tone="blue" />
    </div>
  );
}

function Stat({ label, value, tone = 'gray' }: { label: string; value: string; tone?: 'gray' | 'green' | 'red' | 'blue' }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    gray: { bg: '#f1f5f9', fg: '#334155' },
    green: { bg: '#f0fdf4', fg: '#166534' },
    red: { bg: '#fef2f2', fg: '#b91c1c' },
    blue: { bg: '#eff6ff', fg: '#1e40af' },
  };
  const c = colors[tone];
  return (
    <div style={{ padding: '6px 10px', background: c.bg, color: c.fg, borderRadius: 6 }}>
      <span style={{ opacity: 0.7 }}>{label}: </span>
      <b>{value}</b>
    </div>
  );
}

function ChangeRow({
  row,
  checked,
  onToggle,
}: {
  row: MatchedRow;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <tr>
      <td>
        <input type="checkbox" checked={checked} onChange={onToggle} />
      </td>
      <td style={{ fontFamily: 'monospace', color: '#334155' }}>{row.employeeCode}</td>
      <td>{row.employeeName}</td>
      <td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {Object.entries(row.changes).map(([field, change]) => (
            <div key={field} style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'baseline' }}>
              <span style={{ minWidth: 100, color: '#64748b' }}>{FIELD_LABEL[field] ?? field}:</span>
              <span style={{ color: '#94a3b8', textDecoration: 'line-through' }}>{fmtVal(change.from)}</span>
              <span style={{ color: '#64748b' }}>→</span>
              <span style={{ color: '#166534', fontWeight: 500 }}>{fmtVal(change.to)}</span>
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}

function fmtVal(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return formatVND(v);
  return String(v);
}

const sectionTitle: React.CSSProperties = {
  marginTop: 16,
  marginBottom: 8,
  fontSize: 14,
  fontWeight: 600,
  color: '#334155',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const tableWrap: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  maxHeight: 320,
  overflow: 'auto',
};

const tblStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};
